import { ORG_ID, absoluteUrl } from "@/lib/site";
import { SCAN_DESCRIPTION } from "@/lib/diagnostic";
import { DECISION_SPRINT_LIVE } from "@/lib/flags";
import type { PricingRow } from "@/data/pricing";
import {
  CATALOGUE_ID,
  findRow,
  formatPrice,
  isQuotable,
  offerNodeFor,
  pricingRows,
  slugify,
  unpricedOfferNodeFor,
} from "@/data/pricing";

/**
 * Three tiers, each holding options. A tier answers one owner question, so the
 * options inside a tier are alternatives to each other and the tiers are not.
 * Every figure resolves to exactly one PricingRow. No dollar amount is written
 * in this file.
 */
export type TierOption = {
  id: string;
  name: string;
  plainDescription: string;
  duration: string;
  price: PricingRow;
  path: string;
  /** Compatibility fields for the current /packages render. Derived, not authored. */
  covers: string;
  forWho: string;
  youGet: string[];
  priceTreatment: string;
};

export type Tier = {
  id: "diagnosis" | "ninety-day" | "one-offs" | "retainers";
  name: string;
  ownerQuestion: string;
  forWho: string;
  howPriced: string;
  options: TierOption[];
  /** Compatibility fields for the current /packages render. Derived, not authored. */
  question?: string;
  intro?: string;
};

const opt = (
  id: string,
  rowName: string,
  plainDescription: string,
  duration: string,
  path: string,
): TierOption => {
  const price = findRow(rowName);
  return {
    id,
    name: price.name,
    plainDescription,
    duration,
    price,
    path,
    covers: plainDescription,
    forWho: "",
    youGet: [],
    priceTreatment: "",
  };
};

/** One-off rows charged as a single fixed price below the starter threshold. */
const STARTER_CEILING = 7000;

/** Tiers that place their rows explicitly, so they never fall into the starter list. */
const EXPLICIT_SERVICES = ["Diagnosis", "90-Day Program", "Commercial and Pricing", "Decision Sprint"];

const starterNames = pricingRows
  .filter(
    (row) =>
      row.charge === "one-off" &&
      row.lower !== null &&
      row.lower > 0 &&
      row.lower < STARTER_CEILING &&
      !EXPLICIT_SERVICES.includes(row.service),
  )
  .sort((a, b) => (a.lower ?? 0) - (b.lower ?? 0))
  .map((row) => row.name);


const installNames = pricingRows
  .filter(
    (row) =>
      row.name.toLowerCase().endsWith("install") &&
      row.service !== "90-Day Program" &&
      !starterNames.includes(row.name),
  )
  .sort((a, b) => (a.lower ?? Number.MAX_SAFE_INTEGER) - (b.lower ?? Number.MAX_SAFE_INTEGER))
  .map((row) => row.name);

const starterCopy: Record<string, { plain: string; duration: string; path: string }> = {
  "One process, one fix": {
    plain:
      "We take one process that keeps going wrong, map how it actually runs today, rebuild it, and train the people who use it. You get the written process and one person who owns it.",
    duration: "Two to three weeks.",
    path: "/services/systems-improvement",
  },
  "Quarterly Planning Day": {
    plain:
      "A full day with your leadership team to agree what the next three months are for. You leave with a short written plan, the few priorities that matter, and a name and a date against each one.",
    duration: "One day, plus preparation and a written plan.",
    path: "/services/strategy-execution",
  },
  "Leadership Scorecard Build": {
    plain:
      "We work out the small number of numbers that tell you how the business is really tracking, then build the one-page scorecard your leaders update each week. No new software required.",
    duration: "Two weeks.",
    path: "/services/performance-discipline",
  },
  "Meeting Cadence Install": {
    plain:
      "We fix your meetings. Which ones happen, who is in them, what gets decided, and what gets written down. You get the agendas, the schedule and the standard your team follows after we go.",
    duration: "Two to three weeks.",
    path: "/services/operating-system",
  },
  "Org Design Sprint": {
    plain:
      "We look at who does what today, find the gaps and the overlaps, and redraw the structure. You get a clear chart, a written role for each seat, and a plan for filling the gaps. Includes role requirements, measurable targets and quarterly objectives for each seat.",
    duration: "Two to three weeks.",
    path: "/services/team-structure",
  },
  "1-Day Strategy Session": {
    plain:
      "One day with you and your leaders to settle the direction for the year ahead. Where growth comes from, what you stop doing, and the handful of moves the business will back.",
    duration: "One day, plus a written summary.",
    path: "/services/strategy-execution",
  },
};

const installCopy: Record<string, { plain: string; duration: string; path: string }> = {
  "Service line install": {
    plain:
      "A complete rebuild of one part of the business, from design through to your team running it without us. Which part depends on what is broken.",
    duration: "One quarter for most installs.",
    path: "/services",
  },
  "Systems & Operational Improvement install": {
    plain:
      "We work through the way the work gets done, end to end, and rebuild the parts that cost you time and money. You get written processes, trained people and a named owner for each one.",
    duration: "One quarter.",
    path: "/services/systems-improvement",
  },
  "Performance & Commercial Discipline install": {
    plain:
      "We build the reporting that shows where you make money and where you lose it, then set the weekly and monthly reviews that keep your leaders on top of it.",
    duration: "One quarter.",
    path: "/services/performance-discipline",
  },
  "People, Culture & Accountability install": {
    plain:
      "The roles the business actually needs, what each one is accountable for, the targets that sit against it, and the accountability chart that holds it together. This is not recruitment. It is the structure a hire gets dropped into.",
    duration: "One quarter.",
    path: "/services/team-structure",
  },
  "Operating System & Governance install": {
    plain:
      "We put in the way the business is run week to week: the meetings, the decisions, the reporting and the records. Documented, taught to your team, then handed over.",
    duration: "One quarter.",
    path: "/services/operating-system",
  },
  "Culture and sentiment install": {
    plain:
      "We find out how your people are really going, where the load sits and where the friction is, then work with your leaders to fix it. Priced after a call, because it depends on the size of the team.",
    duration: "Scoped to the size of the team.",
    path: "/services/team-structure",
  },
};

const commercialOptions: TierOption[] = [
  opt(
    "pricing-margin-review",
    "Pricing and Margin Review",
    "Your price architecture, margin waterfall and unit economics reviewed end to end, with a costed set of price moves. Where the money is being left on the table, and what to do about it.",
    "Three to six weeks, subject to size, schedule and availability.",
    "/services/performance-discipline",
  ),
  opt(
    "rolling-cash-forecast",
    "Rolling Cash Forecast, 13 weeks",
    "A 13-week rolling cash forecast built on your actuals, with the operating cadence to keep it current. The most-asked-for finance artefact in a founder-led business.",
    "Two to three weeks.",
    "/services/performance-discipline",
  ),
  opt(
    "annual-operating-budget",
    "Annual Operating Budget",
    "A budget built from operating drivers rather than last year plus a percentage, tied to the plan and to the scorecard that tracks it.",
    "Three to four weeks.",
    "/services/strategy-execution",
  ),
  opt(
    "remuneration-incentive-design",
    "Remuneration and Incentive Design",
    "Bonus and commission structures that pay for the behaviour you actually want, modelled against your P&L before anyone is told about them.",
    "Two to three weeks.",
    "/services/team-structure",
  ),
];

const oneOffOptions: TierOption[] = [
  { ...opt("partner-alignment-review", "Partner Alignment Review", "Agree the six partnership areas and brief your own qualified advisers.", "Typically three to six weeks.", "/services/partner-alignment"), youGet: ["One-on-one with each partner", "Alignment map across the six areas", "Facilitated partner sessions", "Partner Agreement Brief, signed off by every partner", "Adviser briefing and coordination to close", "Partner meeting rhythm and scorecard"] },
  { ...opt("structure-and-performance-reset", "Structure and Performance Reset", "Restructure, redesign roles or address underperformance. The owner decides; the employment lawyer reviews before communication.", "Typically six to nine weeks.", "/services/structure-and-performance-reset"), youGet: ["Structure and accountability map", "Options paper for the owner's decision", "Process and communication plan, lawyer reviewed", "Conversations run with you", "Team reset and refreshed scorecards", "30-day settle check-in"] },
  ...commercialOptions,
  ...starterNames.map((name) => {
    const copy = starterCopy[name];
    if (!copy) throw new Error(`Missing one-off copy for: ${name}`);
    return opt(`starter-${name}`, name, copy.plain, copy.duration, copy.path);
  }),
  opt(
    "ninety-day-execution-sprint",
    "90-Day Execution Sprint",
    "Turn strategy into a 90-day operating rhythm with a few priorities, named owners, clear dates and weekly follow-through across the team.",
    "90 days.",
    "/services/strategy-execution",
  ),
  opt(
    "annual-growth-plan",
    "Annual Growth Plan",
    "The full year set out properly. Where the growth comes from, what it costs, what has to be in place to deliver it, and how you will know each quarter whether you are on track.",
    "Three to four weeks.",
    "/services/strategy-execution",
  ),
  ...installNames.map((name) => {
    const copy = installCopy[name];
    if (!copy) throw new Error(`Missing install copy for: ${name}`);
    return opt(`install-${name}`, name, copy.plain, copy.duration, copy.path);
  }),
];

export const tiers: Tier[] = [
  {
    id: "diagnosis",
    name: "Diagnosis",
    ownerQuestion: "Find out what's actually wrong.",
    forWho: "Owners who suspect the problem is structural but cannot name it.",
    howPriced: "Fixed price, agreed up front.",
    options: [
      opt(
        "operating-scan",
        "Operating Scan",
        SCAN_DESCRIPTION,
        "About six minutes.",
        "/scan",
      ),
      opt(
        "operational-diagnostic",
        "Operating Diagnostic",
        `Desktop diagnostic: Team Survey across your staff (30 statements), Ajility's read of the numbers you supply, and a 90-minute working session with you. Price: ${formatPrice(findRow("Operating Diagnostic"))}.`,
        "Two to three weeks.",
        "/packages",
      ),
      opt(
        "full-diagnostic-roadmap",
        "Full Diagnostic and 90-Day Roadmap",
        "On site: Ajility walks the floor, reads the data and reports, interviews staff and leaders, and hands you a prioritised 90-day roadmap with owners and dates. The from price covers one entity and one site, with up to six interviews and site / office visits, delivered over three to four weeks. Multi-site and group businesses are priced on scope.",
        "Three to four weeks.",
        "/packages",
      ),
    ],
  },
  {
    id: "ninety-day",
    name: "90-Day Program",
    ownerQuestion: "Diagnose it and install it, in one 90-day engagement.",
    forWho: "Owners who already know the business needs a proper operating system and want one fee, one scope and a hard stop.",
    howPriced: "One fixed fee, paid in three instalments across the 90 days. Exit point at Week 3.",
    options: [
      opt(
        "ninety-day-operating-system-install",
        "90-Day Operating System Install",
        "The Full Diagnostic and 90-Day Roadmap in weeks 1 to 3, then the Operating System & Governance install in weeks 4 to 13, run as one engagement. The 90-day plan is executed inside the same 90 days. Includes a review session six weeks after handover.",
        "13 weeks, fixed.",
        "/packages",
      ),
    ],
  },
  {
    id: "one-offs",
    name: "One-offs",
    ownerQuestion: "Fix one thing properly and be done.",
    forWho: "Businesses that already know which part of the operation is broken.",
    howPriced: "Fixed price, agreed up front. It has a defined end.",
    options: oneOffOptions,
  },
  {
    id: "retainers",
    name: "Retainers",
    ownerQuestion: "Run it with me, every month.",
    forWho: "Owners who need more than one part of the business actively led.",
    howPriced:
      "Monthly, composed from an operating rhythm base plus the service lines you want led. Committed one quarter at a time.",
    options: [
      opt(
        "fortnightly-rhythm-base",
        "Fortnightly rhythm base",
        "Every second week we run your leadership meeting, go through the numbers with your team, and hold people to what they said they would do. Each quarter we reset the plan with you.",
        "Ongoing, committed a quarter at a time.",
        "/contact",
      ),
      opt(
        "weekly-rhythm-base",
        "Weekly rhythm base",
        "The same, weekly. Suits businesses moving quickly or coming out of a rough patch, where a fortnight is too long between checks.",
        "Ongoing, committed a quarter at a time.",
        "/contact",
      ),
      opt(
        "additional-service-line",
        "Each additional active service line",
        "On top of the base, we take one part of the business and run it with you month to month, rather than handing you a plan and leaving. Add as many as you need, drop them when they run themselves.",
        "Ongoing, reviewed every quarter.",
        "/services",
      ),
      opt(
        "composed-integrator",
        "Composed Integrator",
        "The rhythm base plus the service lines you want actively led, composed into one monthly fee. Where most retainers land, depending on the mix.",
        "Ongoing, committed a quarter at a time.",
        "/packages",
      ),
      opt(
        "project-leadership",
        "Scoped per initiative",
        "One project that keeps slipping, led properly. We set the plan, run the weekly delivery meeting, chase what is stuck and report progress to you or your board until it lands.",
        "A quarter or longer.",
        "/services/project-leadership",
      ),
    ],
  },
];

// Derive the compatibility fields the current /packages render reads.
for (const tier of tiers) {
  tier.question = tier.ownerQuestion;
  tier.intro = `${tier.forWho} ${tier.howPriced}`;
  for (const option of tier.options) {
    option.forWho = tier.forWho;
    option.priceTreatment = tier.howPriced;
  }
}

/** Every option, flattened, in tier order. */
export const tierOptions: TierOption[] = tiers.flatMap((tier) => tier.options);

const describe = (option: TierOption, tier: Tier): string => {
  if (option.id === "full-diagnostic-roadmap") return option.plainDescription;
  const price = formatPrice(option.price);
  const base = `${option.name}, part of the ${tier.name} tier at Ajility. ${option.plainDescription}`;
  return price
    ? `${base} Priced ${price === "Free" ? "free" : price}.`
    : `${base} Scoped to the size and complexity of the business. Pricing is confirmed before any work begins.`;
};

/**
 * The catalogue is generated from the same tiers the page renders, so structured
 * data can never publish a product or a price the page does not show.
 */
export const offerCatalogSchema = () => {
  const seen = new Set<string>();
  const nodes: unknown[] = [];

  for (const tier of tiers) {
    for (const option of tier.options) {
      if (!DECISION_SPRINT_LIVE && option.price.service === "Decision Sprint") continue;
      if (seen.has(option.id)) continue;
      seen.add(option.id);
      const description = describe(option, tier);
      const meta = { id: option.id, category: tier.name };
      nodes.push(
        isQuotable(option.price)
          ? offerNodeFor(option.price, description, meta)
          : unpricedOfferNodeFor(option.price, description, meta),
      );
    }
  }


  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "@id": CATALOGUE_ID,
    name: "Ajility published pricing",
    description:
      "Ajility works in three tiers: Diagnosis, One-offs and Retainers. Work running a quarter or longer is charged per month, shorter work is a fixed one-off price. All figures are quoted plus GST.",
    url: absoluteUrl("/packages"),
    provider: { "@id": ORG_ID },
    itemListElement: nodes,
  };
};

/**
 * Compatibility helpers for the current /packages render, which is rewritten in
 * a later change. They derive from the tier data above and publish no figures of
 * their own.
 */
export const optionPriceRow = (option: TierOption): PricingRow => option.price;
export const optionName = (option: TierOption): string => option.name;
export const optionComponents = (_option: TierOption): PricingRow[] => [];

/**
 * The catalogue @ids declared for a given page path. The OfferCatalog on
 * /packages is the single place a product is declared as a full Offer node, so
 * any other page describing the same product references these ids instead of
 * declaring a second node. Derived from the same tier data the catalogue is
 * built from, so it stays correct for future products with no page edits.
 */
export const catalogueOfferIdsForPath = (path: string): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const tier of tiers) {
    for (const option of tier.options) {
      const optionPath = option.path || option.price.path;
      if (optionPath !== path || seen.has(option.id)) continue;
      seen.add(option.id);
      ids.push(`${absoluteUrl("/packages")}#offer-${slugify(option.id)}`);
    }
  }
  return ids;
};
